import React from "react";
import { Helmet } from "react-helmet-async";
import { HreflangTags } from "../components/SeoHreflang";
import { Container, Box, Typography, Button, Avatar, Paper, Grid, Divider } from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import { usePricingNav } from "../hooks/usePricingNav";
import StarIcon from "@mui/icons-material/Star";
import FormatQuoteIcon from "@mui/icons-material/FormatQuote";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { DP } from "../components/home/HomeStyles";

interface TestimonialData {
  name: string;
  roleKey: "owner" | "regular" | "longTime";
  rating: number;
  text: string;
  pet: string;
  color: string;
}

const TestimonialDetail: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const goToPricing = usePricingNav();
  
  const { testimonial, role } = location.state || { testimonial: null, role: "" };

  if (!testimonial) {
    return (
      <Box sx={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", backgroundColor: DP.blueBg }}>
        <Container maxWidth="sm">
          <Typography variant="h5" sx={{ textAlign: "center", color: DP.textDark, mb: 3 }}>
            Testimonial not found
          </Typography>
          <Button variant="contained" onClick={() => navigate("/")} fullWidth>
            Back to Home
          </Button>
        </Container>
      </Box>
    );
  }

  const item: TestimonialData = testimonial;

  return (
    <Box sx={{ minHeight: "100vh", backgroundColor: DP.blueBg, pt: 8, pb: 8 }}>
      <Helmet>
        <title>{item.name}'s Review — Dr.Paw</title>
        <meta name="description" content={`Read ${item.name}'s testimonial about their experience with Dr.Paw veterinary care.`} />
        <meta name="robots" content="noindex, follow" />
      </Helmet>
      <HreflangTags />
      <Container maxWidth="md">
        {/* Back button */}
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate("/")}
          sx={{
            mb: 4,
            color: DP.blue,
            textTransform: "none",
            fontWeight: 600,
            fontSize: "0.95rem",
            "&:hover": { backgroundColor: "rgba(21,101,192,0.06)" },
          }}
        >
          Back to Home
        </Button>

        {/* Main card */}
        <Paper
          elevation={0}
          sx={{
            backgroundColor: DP.white,
            borderRadius: "28px",
            p: { xs: 4, md: 6 },
            border: `1px solid ${DP.border}`,
            boxShadow: `0 8px 32px rgba(21,101,192,0.1)`,
            mb: 4,
          }}
        >
          {/* Header with avatar and name */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 3, mb: 4 }}>
            <Avatar
              sx={{
                width: 80,
                height: 80,
                backgroundColor: item.color,
                fontSize: "2rem",
                fontWeight: 700,
                color: DP.blue,
              }}
            >
              {item.name.charAt(0).toUpperCase()}
            </Avatar>
            <Box>
              <Typography variant="h4" sx={{ fontWeight: 800, color: DP.textDark, mb: 0.5 }}>
                {item.name}
              </Typography>
              <Typography sx={{ color: DP.textMuted, fontSize: "0.95rem", mb: 1 }}>
                {role}
              </Typography>
              <Box sx={{ display: "flex", gap: 0.3 }}>
                {Array.from({ length: item.rating }).map((_, i) => (
                  <StarIcon key={i} sx={{ color: "#FFC107", fontSize: "1.2rem" }} />
                ))}
              </Box>
            </Box>
          </Box>

          <Divider sx={{ my: 4 }} />

          {/* Review content */}
          <Box sx={{ mb: 4 }}>
            <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2, mb: 3 }}>
              <Box
                sx={{
                  width: 52,
                  height: 52,
                  borderRadius: "14px",
                  backgroundColor: item.color,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <FormatQuoteIcon sx={{ color: DP.blue, fontSize: "1.8rem" }} />
              </Box>
              <Box>
                <Typography
                  sx={{
                    fontSize: "1.15rem",
                    lineHeight: 1.9,
                    color: DP.textMid,
                    fontStyle: "italic",
                    fontWeight: 500,
                  }}
                >
                  "{item.text}"
                </Typography>
              </Box>
            </Box>
          </Box>

          <Divider sx={{ my: 4 }} />

          {/* Pet emoji and details */}
          <Grid container spacing={3} sx={{ mt: 2 }}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Box
                sx={{
                  p: 3,
                  borderRadius: "16px",
                  backgroundColor: `${item.color}40`,
                  border: `1px solid ${item.color}60`,
                  textAlign: "center",
                }}
              >
                <Typography sx={{ fontSize: "2.8rem", mb: 1 }}>
                  {item.pet}
                </Typography>
                <Typography sx={{ color: DP.textMuted, fontSize: "0.9rem", fontWeight: 600 }}>
                  Pet Owner
                </Typography>
              </Box>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Box
                sx={{
                  p: 3,
                  borderRadius: "16px",
                  backgroundColor: "rgba(76,175,80,0.1)",
                  border: "1px solid rgba(76,175,80,0.3)",
                }}
              >
                <Typography sx={{ color: "#4CAF50", fontWeight: 700, fontSize: "1.1rem", mb: 1 }}>
                  ✓ Verified Review
                </Typography>
                <Typography sx={{ color: DP.textMuted, fontSize: "0.85rem" }}>
                  Google Maps Review
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Paper>

        {/* CTA section */}
        <Paper
          elevation={0}
          sx={{
            backgroundColor: `linear-gradient(135deg, ${DP.blue} 0%, #42A5F5 100%)`,
            borderRadius: "24px",
            p: { xs: 4, md: 5 },
            textAlign: "center",
            color: DP.white,
          }}
        >
          <Typography variant="h5" sx={{ fontWeight: 800, mb: 2 }}>
            Experience the Same Care
          </Typography>
          <Typography sx={{ fontSize: "1.05rem", mb: 3, opacity: 0.95 }}>
            Join hundreds of satisfied pet owners who trust Dr. Paw for their pet's health and wellness.
          </Typography>
          <Button
            variant="contained"
            size="large"
            onClick={() => goToPricing()}
            sx={{
              backgroundColor: DP.white,
              color: DP.blue,
              fontWeight: 700,
              fontSize: "1rem",
              px: 4,
              py: 1.5,
              borderRadius: "50px",
              textTransform: "none",
              "&:hover": {
                backgroundColor: "#f5f5f5",
              },
            }}
          >
            Book Your Visit Today
          </Button>
        </Paper>
      </Container>
    </Box>
  );
};

export default TestimonialDetail;
